import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  createUser,
  deleteUser,
  getUsers,
  updateUser,
} from '../api/endpoints/users';
import { confirmDelete, showSuccess, showError } from '../utils/alerts';

type UserStatus = 'Active' | 'Inactive';
type User = {
  id: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  status: UserStatus;
};
type UserFormState = {
  name: string;
  email: string;
  phone: string;
  role: string;
  status: UserStatus;
  profile_photo: File | string | null;
  password?: string;
};

const emptyForm: UserFormState = {
  name: '',
  email: '',
  phone: '',
  role: 'user',
  status: 'Active',
  profile_photo: null,
  password: '',
};

const Users: React.FC = () => {
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formState, setFormState] = useState<UserFormState>(emptyForm);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const fetchUsers = useCallback(
    async (page = 1, searchVal = '', roleVal = '') => {
      setLoading(true);
      try {
        const params: Record<string, any> = { page };
        if (searchVal.trim()) params.search = searchVal.trim();
        if (roleVal) params.role = roleVal;
        const { list, meta } = await getUsers(params);
        setData(list as User[]);
        setTotalPages(meta?.last_page || 1);
        setCurrentPage(meta?.current_page || 1);
      } catch (err) {
        showError('Sync failed');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(1, search, roleFilter);
    }, 400);
    return () => clearTimeout(timer);
  }, [search, roleFilter, fetchUsers]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((p) => ({ ...p, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormState((p) => ({ ...p, profile_photo: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.name || !formState.email || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = new FormData();
      Object.entries(formState).forEach(([k, v]) => {
        if (v !== null && v !== '')
          payload.append(k, v instanceof window.File ? v : String(v));
      });
      payload.append('full_name', formState.name);
      payload.append(
        'is_active',
        String(formState.status === 'Active' ? 1 : 0),
      );

      if (editingId) await updateUser(editingId, payload);
      else await createUser(payload);

      showSuccess(editingId ? 'User Updated' : 'User Created');
      setIsModalOpen(false);
      fetchUsers(currentPage, search, roleFilter);
    } catch (err: any) {
      if (err.response?.data?.error) {
        const msgs = Object.values(err.response.data.error).flat().join(' | ');
        showError(msgs);
      } else {
        showError(err.response?.data?.message || 'Save Failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (u: User) => {
    if (await confirmDelete(`Delete "${u.name}"?`)) {
      try {
        await deleteUser(u.id);
        showSuccess('User deleted');
        fetchUsers(currentPage, search, roleFilter);
      } catch (err) {
        showError('Failed');
      }
    }
  };

  const clearFilters = () => {
    setSearch('');
    setRoleFilter('');
  };

  return (
    <section className="cyber-section-card">
      <div className="crud-page-header">
        <div>
          <p className="cyber-page-kicker">Access Control</p>
          <h1 className="cyber-standalone-title">Users</h1>
        </div>
        <button
          className="crud-add-button"
          onClick={() => {
            setEditingId(null);
            setFormState(emptyForm);
            setIsModalOpen(true);
          }}
        >
          + Create Account
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="crud-filters">
        <input
          className="crud-search-input"
          placeholder="🔍 Search by name, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="crud-filter-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">All Roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="company">Company</option>
          <option value="user">User</option>
        </select>
        {(search || roleFilter) && (
          <button className="crud-action-button" onClick={clearFilters}>
            ✕ Clear
          </button>
        )}
      </div>

      <div className="crud-table-wrap">
        <table className="crud-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Contact</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  Syncing...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  No users found
                </td>
              </tr>
            ) : (
              data.map((u) => (
                <tr key={u.id}>
                  <td>
                    <span className="row-tag">#{u.id}</span>
                  </td>
                  <td>
                    <div className="row-main-text">{u.name}</div>
                    <div className="row-sub-text">{u.role}</div>
                  </td>
                  <td>
                    <div className="row-main-text">{u.email}</div>
                    <div className="row-sub-text">{u.phone}</div>
                  </td>
                  <td>{u.role}</td>
                  <td>
                    <span
                      className={`crud-status-badge ${
                        u.status === 'Active'
                          ? 'crud-status-active'
                          : 'crud-status-inactive'
                      }`}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td>
                    <div className="crud-actions">
                      <button
                        className="crud-action-button"
                        onClick={() => {
                          setEditingId(u.id);
                          setFormState({
                            ...u,
                            profile_photo: null,
                            password: '',
                          });
                          setIsModalOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="crud-action-button crud-action-danger"
                        onClick={() => handleDelete(u)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="crud-pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => fetchUsers(currentPage - 1, search, roleFilter)}
            >
              Prev
            </button>
            <span>
              {currentPage} / {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => fetchUsers(currentPage + 1, search, roleFilter)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen &&
        createPortal(
          <div
            className="crud-modal-overlay"
            onMouseDown={(e) =>
              e.target === e.currentTarget && setIsModalOpen(false)
            }
          >
            <div className="crud-modal-panel">
              <div className="crud-modal-head">
                <h2>{editingId ? 'Modify' : 'New'} Account</h2>
                <button
                  className="crud-modal-close"
                  onClick={() => setIsModalOpen(false)}
                >
                  X
                </button>
              </div>
              <form className="crud-form" onSubmit={handleSubmit}>
                <div className="form-grid">
                  <label className="crud-field">
                    <span>Full Name</span>
                    <input
                      name="name"
                      value={formState.name}
                      onChange={handleInputChange}
                      required
                    />
                  </label>
                  <label className="crud-field">
                    <span>Email Identity</span>
                    <input
                      type="email"
                      name="email"
                      value={formState.email}
                      onChange={handleInputChange}
                      required
                    />
                  </label>
                </div>
                <div className="form-grid">
                  <label className="crud-field">
                    <span>Phone</span>
                    <input
                      name="phone"
                      value={formState.phone}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label className="crud-field">
                    <span>Role</span>
                    <select
                      name="role"
                      value={formState.role}
                      onChange={handleInputChange}
                    >
                      <option value="company">Company</option>
                      <option value="super_admin">Super Admin</option>
                      <option value="user">User</option>
                    </select>
                  </label>
                </div>
                <label className="crud-field">
                  <span>Password {editingId && '(Leave blank to keep)'}</span>
                  <input
                    type="password"
                    name="password"
                    value={formState.password || ''}
                    onChange={handleInputChange}
                    required={!editingId}
                    placeholder="••••••••"
                  />
                </label>
                <div className="form-grid">
                  <label className="crud-field">
                    <span>Status</span>
                    <select
                      name="status"
                      value={formState.status}
                      onChange={handleInputChange}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </label>
                  <label className="crud-field">
                    <span>Profile Photo</span>
                    <input
                      type="file"
                      className="crud-file-input"
                      name="profile_photo"
                      onChange={handleFileChange}
                      accept="image/*"
                    />
                  </label>
                </div>
                <div className="crud-modal-actions">
                  <button
                    type="button"
                    className="crud-action-button"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="crud-add-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Deploy Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
};

export default Users;
